import { useMutation } from "@tanstack/react-query";
import {
  AuthService,
  type OtpVerifyFailureData,
} from "@/services/auth-service";
import { useProfileStore } from "@/store/profile-store";
import { User } from "@/models/auth/User";

// Default resend cooldown when the backend doesn't hand us a `retry_after`
// (e.g. on a successful send). The server is the source of truth otherwise.
export const DEFAULT_RESEND_SECONDS = 60;

export interface OtpRequestError {
  kind: "invalid" | "cooldown";
  message: string;
  // seconds to wait before the next send is allowed (cooldown only)
  retryAfter?: number;
}

export interface OtpVerifyError {
  kind: "verify_failed";
  status: number;
  message: string;
  remainingAttempts?: number;
  // true when the code is gone (burned/expired/never requested) — the UI must
  // send the user back to "request a new code".
  reset: boolean;
}

/**
 * Request an OTP for a phone number. Resolves with the number of seconds to
 * keep the "resend" button disabled; rejects with an {@link OtpRequestError}.
 */
export function useRequestOtp() {
  return useMutation<number, OtpRequestError, string>({
    mutationFn: async (phone) => {
      const res = await AuthService.requestOtp(phone);

      // Generic success — a code was sent if the number is valid.
      if (res.success) {
        return res.data?.retry_after ?? DEFAULT_RESEND_SECONDS;
      }

      // 429: resend cooldown or rate limit. fetchInstance can't read the bare
      // `Retry-After` header, so fall back to the default when `data` is empty.
      if (res.status === 429) {
        throw {
          kind: "cooldown",
          message:
            res.message ??
            "تعداد درخواست‌ها بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.",
          retryAfter: res.data?.retry_after ?? DEFAULT_RESEND_SECONDS,
        } satisfies OtpRequestError;
      }

      // 422 / anything else: invalid phone or unexpected error.
      throw {
        kind: "invalid",
        message: res.message ?? "شماره تماس نامعتبر است.",
      } satisfies OtpRequestError;
    },
  });
}

/**
 * Verify the OTP and establish a session. On success the profile store is
 * populated (tokens persisted to localStorage) and the logged-in {@link User}
 * is returned; on failure it rejects with an {@link OtpVerifyError}.
 */
export function useVerifyOtp() {
  const setTokens = useProfileStore((s) => s.setTokens);
  const setProfile = useProfileStore((s) => s.setProfile);

  return useMutation<User, OtpVerifyError, { phone: string; code: string }>({
    mutationFn: async ({ phone, code }) => {
      const res = await AuthService.verifyOtp(phone, code);

      if (res.success && res.data) {
        setTokens({
          access_token: res.data.access_token,
          refresh_token: res.data.refresh_token,
        });
        setProfile(res.data.user);
        return res.data.user;
      }

      // On failure the envelope's `data` carries `remaining_attempts` (wrong
      // code) or is null (expired / not found). A null data, a 0-attempt
      // response, or any 429 means the code is burned → restart the flow.
      const failData = res.data as unknown as OtpVerifyFailureData | null;
      const remainingAttempts = failData?.remaining_attempts;
      const reset =
        res.status === 429 || remainingAttempts === 0 || failData == null;

      throw {
        kind: "verify_failed",
        status: res.status,
        message: res.message ?? "کد تایید نادرست است.",
        remainingAttempts,
        reset,
      } satisfies OtpVerifyError;
    },
  });
}
